// A sheet's own objects under the pointer (docs/claude/sheets.md): picking their
// grips, vertices and badges, snapping on paper, the press / drag / release on
// them, finishing what a paper tool drew, the text box's inline editor, their
// clipboard and style copy, and the drafts drawn while a tool is under way.
import {
  type SchematicBox,
  type SchematicConn,
  type SchematicDim,
  type SchematicImage,
  type SchematicLine,
  type SchematicLineStyle,
  type Sheet,
  type Vec2,
} from '../../shared/types';
import {
  clipboardUi,
  flashClipboardElsewhere,
  takeClipboard,
} from './clipboard';
import { squareToNeighbours } from '../connDrag';
import {
  coreMenuItems,
  type CtxItem,
  openContextMenu,
} from './contextMenus';
import {
  beginGesture,
  commitGesture,
  drawingRev,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  distEntryPoint,
  updateDistEntry,
} from './distanceEntry';
import { elecPickOf } from '../elecLayout';
import {
  elecDeletePick,
  elecMenuItems,
  elecSheet,
  elecViewAt,
  elecViewById,
  elecViewsGo,
} from './elecSheet';
import {
  draftSeg,
  inferDimKind,
  nearestFractionOnPath,
  nearestVertexSnap,
  pointAtFraction,
  snapDraftGuides,
} from '../geometry';
import { GRIP_HIT_PX } from '../grip';
import { tr } from '../i18n';
import { type EditorLook } from '../inlineText';
import { paperDimOffset } from '../paperDim';
import { addPoint } from '../polylineDraft';
import {
  drawHoverOutline,
  drawMarquee,
  drawPendingLine,
  drawPendingTextBox,
} from '../render';
import {
  boxFontMm,
  boxFontPt,
  drawPaperDim,
  schBadgePx,
  schBadgeT,
  schBoxCenter,
  schConnRoute,
  type SchemSel,
  schHitObject,
  schSelRect,
} from '../schematic';
import {
  copySheetObjects,
  deletableSheetObjects,
  deleteSheetObjects,
  moveFromSnapshot,
  pasteSheetObjects,
  schContent,
  sheetClipBounds,
  sheetSnapPoints,
  type SheetSnapshot,
  snapshotSheetObjects,
} from '../sheetObjects';
import {
  fitViewportToBounds,
  type HandleId,
  makeViewport,
  paperToScreen,
  type PaperView,
  rectHandles,
  resizeRect,
  screenToPaper,
  viewportFrame,
} from '../sheets';
import { snapCandidates } from './snapSet';
import { titleEditor } from './titleEditor';
import {
  ALIGN_TOL_PX,
  alignMoveSnap,
  type AlignRect,
  alignResizeSnap,
  ctx,
  draftLockStep,
  flashToolStatus,
  paperPolyline,
  screenPos,
  textEditorUi,
  undoSteps,
} from '../main';
import {
  activeSheet,
  clampViewportToContent,
  endPaperOneShot,
  modelBoundsOrNull,
  paperView,
  schemSel,
  schemSelHas,
  schemSelSet,
  schLineClosed,
  schTool,
  setSchemSel,
  setSchemSelSet,
  SHEET_OBJECTS,
  sheetHover,
  sheetTableAt,
  toggleSchemSel,
} from './sheetSpace';

/** This module's state that main.ts reads and writes too. */
export const sheetSchematic = {
  /** The paper Dimension being placed: its two points (the third click sets the offset). */
  schDimDraft: null as { a?: Vec2; b?: Vec2 } | null,
  schConnDraft: null as { fromId: string; to: Vec2 } | null,
  /** A marquee on a sheet (paper mm), started on empty paper; `add` = Shift. */
  sheetMarquee: null as { a: Vec2; b: Vec2; add: boolean } | null,
  /** In-progress schematic line vertices / connection drag (paper mm). */
  schLineDraft: [] as Vec2[],
  // In-progress schematic text box — the same shape as the model's `textDraft`,
  // and placed by the same click / click / click-for-a-leader gesture.
  schBoxDraft: null as { a?: Vec2; b?: Vec2; leader?: Vec2 } | null,
};

// ---- Schematic sheet interaction (paper-mm space) ----
type SchDrag =
  | { mode: 'badge'; id: string }
  | { mode: 'move-line'; id: string; grab: Vec2; pts0: Vec2[] }
  | { mode: 'move-multi'; grab: Vec2; items: SheetSnapshot[] }
  | { mode: 'move-rect'; sel: SchemSel; grab: Vec2; x0: number; y0: number }
  | { mode: 'resize'; sel: SchemSel; handle: string; orig: { x: number; y: number; w: number; h: number } }
  | { mode: 'vertex'; kind: 'line' | 'conn'; id: string; index: number };
export let schDrag: SchDrag | null = null;
export let schHover: Vec2 | null = null;
export let schEditBoxId: string | null = null;
// Blinking caret for the box being edited: the caret is painted on the canvas
// (the DOM editor's own caret is hidden), so a timer toggles its ON phase.
export let schCaretOn = true;
let schCaretTimer: number | null = null;
function stopSchCaretBlink(): void { if (schCaretTimer != null) { clearInterval(schCaretTimer); schCaretTimer = null; } schCaretOn = true; }

export const schUid = (p: string): string => newId(p);
// The lookups and hit tests read a sheet's drawn objects without making them: a
// press or a panel on a sheet with none (only a view or a viewport) writes nothing.
export function schGetBox(sh: Sheet, id: string): SchematicBox | undefined { return sh.schematic?.boxes.find((b) => b.id === id); }
function schGetLine(sh: Sheet, id: string): SchematicLine | undefined { return sh.schematic?.lines.find((l) => l.id === id); }
function schGetImage(sh: Sheet, id: string): SchematicImage | undefined { return sh.schematic?.images.find((im) => im.id === id); }

/** Normalised rects for every schematic box on `sh` except `exceptId` (paper Y-down). */
function otherSchBoxRects(sh: Sheet, exceptId: string): AlignRect[] {
  const out: AlignRect[] = [];
  for (const b of sh.schematic?.boxes ?? []) {
    if (b.id === exceptId) continue;
    out.push({ x0: b.x, x1: b.x + b.w, y0: b.y, y1: b.y + b.h });
  }
  return out;
}

// Pick radius (screen px) for schematic handles / vertices — a bit larger than
// the drawn handle so they're easy to grab with a real mouse.
const SCH_HANDLE_HIT = GRIP_HIT_PX;

/** Resize handle of the selected box/image under a screen point, or null. */
export function schHandleAt(sh: Sheet, pv: PaperView, sp: Vec2): string | null {
  if (!schemSel) return null;
  const r = schSelRect(sh, schemSel);
  if (!r) return null;
  const a = paperToScreen(pv, r.x, r.y);
  for (const h of rectHandles({ x: a.x, y: a.y, w: r.w * pv.scale, h: r.h * pv.scale })) if (Math.abs(h.x - sp.x) <= SCH_HANDLE_HIT && Math.abs(h.y - sp.y) <= SCH_HANDLE_HIT) return h.id;
  return null;
}

/** Vertex of the selected line/connection under a screen point, or null. */
export function schVertexAt(sh: Sheet, pv: PaperView, sp: Vec2): { kind: 'line' | 'conn'; id: string; index: number } | null {
  if (!schemSel) return null;
  const pick = (pts: Vec2[], kind: 'line' | 'conn', id: string): { kind: 'line' | 'conn'; id: string; index: number } | null => {
    for (let i = 0; i < pts.length; i++) { const s = paperToScreen(pv, pts[i].x, pts[i].y); if (Math.abs(s.x - sp.x) <= SCH_HANDLE_HIT && Math.abs(s.y - sp.y) <= SCH_HANDLE_HIT) return { kind, id, index: i }; }
    return null;
  };
  if (schemSel.kind === 'line') { const l = schGetLine(sh, schemSel.id); if (l) return pick(l.points, 'line', l.id); }
  if (schemSel.kind === 'conn') { const c = sh.schematic?.conns.find((x) => x.id === schemSel!.id); if (c?.waypoints) return pick(c.waypoints, 'conn', c.id); }
  return null;
}

/** The connection whose badge is under the screen point (for click-drag along the
 *  route), or null. Tests every connection's badge — drawn on top of the routes —
 *  so it can be grabbed directly without selecting the line first. The badge box is
 *  wider than tall, so the horizontal tolerance is looser. */
export function schBadgeAt(sh: Sheet, pv: PaperView, sp: Vec2): string | null {
  const px = Math.max(6, schBadgePx(pv.scale));
  const hw = Math.max(SCH_HANDLE_HIT, px * 1.6), hh = Math.max(SCH_HANDLE_HIT, px);
  for (const c of sh.schematic?.conns ?? []) {
    const route = schConnRoute(sh, c);
    if (!route) continue;
    const at = pointAtFraction(route.map((p) => paperToScreen(pv, p.x, p.y)), schBadgeT(c));
    if (Math.abs(at.x - sp.x) <= hw && Math.abs(at.y - sp.y) <= hh) return c.id;
  }
  return null;
}

/** A routing waypoint of any connection under a screen point (for right-click
 *  removal), or null. */
export function schWaypointAt(sh: Sheet, pv: PaperView, sp: Vec2): { connId: string; index: number } | null {
  for (const c of sh.schematic?.conns ?? []) {
    const wps = c.waypoints; if (!wps) continue;
    for (let i = 0; i < wps.length; i++) {
      const s = paperToScreen(pv, wps[i].x, wps[i].y);
      if (Math.abs(s.x - sp.x) <= SCH_HANDLE_HIT && Math.abs(s.y - sp.y) <= SCH_HANDLE_HIT) return { connId: c.id, index: i };
    }
  }
  return null;
}

/** Vertices on the page a new line point can snap or align to: the other
 *  schematic lines' points and the corners of the boxes, images and viewports —
 *  the paper-space counterpart of the model's `nearbyOrthoVerts`. */
function schSnapVerts(sh: Sheet): Vec2[] {
  return sheetSnapPoints(sh, SHEET_OBJECTS);
}

/** The proximity ("object") snap on a sheet: the nearest schematic / viewport
 *  corner within 8 px. This is the paper-space counterpart of the model's
 *  `snapPoint`, and the text tool uses it alone — exactly as the model's text
 *  tool snaps its corners with `snapPoint` and no alignment guides. */
function schHoverPoint(paper: Vec2, shift: boolean): Vec2 {
  if (schDrag) return paper;
  if (schTool === 'line') return schLineSnap(paper, shift);
  if (schTool === 'text' || schTool === 'rect' || schTool === 'ellipse' || schTool === 'leader' || schTool === 'viewport') return schPointSnap(paper, shift);
  if (schTool === 'dim') return schDimSnap(paper, shift);
  return paper;
}

function schPointSnap(paper: Vec2, shift: boolean): Vec2 {
  const sh = activeSheet();
  if (shift || !sh) return paper;
  return nearestVertexSnap(paper, schSnapVerts(sh), 8 / paperView().scale);
}

/**
 * The sheet line tool's snap. Deliberately the SAME resolver the model line tool
 * uses — `snapDraftGuides`, at the page's scale instead of the model's — so the
 * two tools offer identical alignment, 90°-normal, 45° and ortho/polar behaviour
 * and Alt suspends both the same way.
 */
function schLineSnap(paper: Vec2, shift: boolean): Vec2 {
  if (shift) return paper;
  const sh = activeSheet();
  const cands = [...sheetSchematic.schLineDraft, ...(sh ? schSnapVerts(sh) : [])];
  const thr = 12 / paperView().scale;
  return snapDraftGuides(nearestVertexSnap(paper, cands, 8 / paperView().scale), cands, thr, {
    allow45: true,
    lastSeg: draftSeg(sheetSchematic.schLineDraft),
    lockStep: draftLockStep(),
  });
}

/**
 * Snap for MOVING an existing line vertex — deliberately the SAME resolver as
 * adding one (`schLineSnap`), so a dragged vertex offers identical proximity /
 * alignment / 90°-normal / 45° / ortho-polar behaviour. The points before the
 * moved one act as the "draft so far" (its preceding segment anchors the guides,
 * exactly like the last drawn segment does when appending), and every other snap
 * vertex on the page is a candidate — minus the point being dragged, so it can't
 * snap to itself. Alt suspends, as everywhere else.
 */
function schLineVertexSnap(paper: Vec2, line: SchematicLine, index: number, shift: boolean): Vec2 {
  if (shift) return paper;
  const sh = activeSheet();
  if (!sh) return paper;
  const moving = line.points[index];
  const cands = schSnapVerts(sh).filter((v) => v !== moving);
  const thr = 12 / paperView().scale;
  return snapDraftGuides(nearestVertexSnap(paper, cands, 8 / paperView().scale), cands, thr, {
    allow45: true,
    lastSeg: draftSeg(line.points.slice(0, index)),
    lockStep: draftLockStep(),
  });
}

/** Schematic-object interaction on a sheet. Returns true when it consumed the
 *  press; false when the Select tool clicked empty space, so the caller can fall
 *  through to viewport / page handling. */
export function schematicMouseDown(e: MouseEvent, sh: Sheet, pv: PaperView): boolean {
  if (e.button !== 0) return false; // right-click finishes a line (handled in the contextmenu listener)
  const sp = screenPos(e);
  const paper = screenToPaper(pv, sp);
  if (schEditBoxId) schCloseBoxEditor(true);
  // Grabbing a visible handle / vertex of the current selection always resizes /
  // reshapes it — even while a create tool (text/line/connection) is active — so
  // the handles you see are always draggable. Only if the cursor isn't on one do
  // the create tools take over.
  const handle = schHandleAt(sh, pv, sp);
  if (handle && schemSel) { beginGesture(); schDrag = { mode: 'resize', sel: schemSel, handle, orig: schSelRect(sh, schemSel)! }; return true; }
  const vtx = schVertexAt(sh, pv, sp);
  if (vtx) { beginGesture(); schDrag = { mode: 'vertex', kind: vtx.kind, id: vtx.id, index: vtx.index }; return true; }
  // Grab a connection badge to slide it along its route (Select tool only, so it
  // doesn't swallow a click meant to place a line point / start a connection).
  if (schTool === 'select') {
    const badge = schBadgeAt(sh, pv, sp);
    if (badge) { setSchemSel({ kind: 'conn', id: badge }); beginGesture(); schDrag = { mode: 'badge', id: badge }; renderProperties(); invalidate(); return true; }
  }
  if (schTool === 'dim') {
    // The model Dimension's gesture: two points, then where the line sits (which
    // also picks horizontal / vertical / aligned, `inferDimKind`).
    const w = schDimSnap(paper, e.altKey);
    const d = (sheetSchematic.schDimDraft ??= {});
    if (!d.a) d.a = w;
    else if (!d.b) d.b = w;
    else schFinishDim(sh, d.a, d.b, paper);
    invalidate();
    return true;
  }
  if (schTool === 'ellipse') {
    // The model Circle's gesture: the centre, then a point on the rim.
    const w = schPointSnap(paper, e.altKey);
    const d = (sheetSchematic.schBoxDraft ??= {});
    if (!d.a) d.a = w;
    else { d.b = w; schFinishCircle(sh); }
    invalidate();
    return true;
  }
  if (schTool === 'leader') {
    // The model Leader's order: the arrow tip first, then the text box's corners.
    const w = schPointSnap(paper, e.altKey);
    const d = (sheetSchematic.schBoxDraft ??= {});
    if (!d.leader) d.leader = w;
    else if (!d.a) d.a = w;
    else { d.b = w; schFinishTextBox(sh); }
    invalidate();
    return true;
  }
  if (schTool === 'rect' || schTool === 'viewport') {
    const w = schPointSnap(paper, e.altKey);
    const d = (sheetSchematic.schBoxDraft ??= {});
    if (!d.a) d.a = w;
    else { d.b = w; if (schTool === 'rect') schFinishRect(sh); else schFinishViewport(sh); }
    invalidate();
    return true;
  }
  if (schTool === 'text') {
    const w = schPointSnap(paper, e.altKey);
    const d = (sheetSchematic.schBoxDraft ??= {});
    if (!d.a) d.a = w;
    else if (!d.b) d.b = w;
    else { d.leader = w; schFinishTextBox(sh); }
    invalidate();
    return true;
  }
  if (schTool === 'line') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    // On the first point a polygon closes (polylineDraft.ts), as the model's does.
    const pl = paperPolyline(sh);
    addPoint(pl.rule, pl.draft, schLineSnap(paper, e.altKey), 12 / pv.scale);
    invalidate();
    return true;
  }
  if (schTool === 'connection') { const hit = schHitObject(sh, paper, 3 / pv.scale); if (hit?.kind === 'box') sheetSchematic.schConnDraft = { fromId: hit.id, to: paper }; return true; }
  // Select tool: whole-object move → else fall through to viewport / page handling.
  const hit = schHitObject(sh, paper, 3 / pv.scale);
  const additive = e.shiftKey || e.ctrlKey || e.metaKey;
  if (!hit) {
    // Shift on empty paper: a marquee that ADDS (a bare click keeps the selection).
    if (additive) { sheetSchematic.sheetMarquee = { a: paper, b: paper, add: true }; return true; }
    if (schemSelSet.length) { setSchemSel(null); renderProperties(); invalidate(); }
    return false;                             // plain empty click → viewport / page handling
  }
  if (additive) { toggleSchemSel(hit); renderProperties(); invalidate(); return true; }
  // A plain click on a member of the current multi-selection keeps the whole set
  // (and drags it together); otherwise it selects just this object.
  if (!(schemSelSet.length > 1 && schemSelHas(hit))) setSchemSel(hit);
  if (schemSelSet.length > 1) { beginSchMultiMove(sh, paper); }
  else if (hit.kind === 'box' || hit.kind === 'image') { const r = schSelRect(sh, hit)!; beginGesture(); schDrag = { mode: 'move-rect', sel: hit, grab: paper, x0: r.x, y0: r.y }; }
  else if (hit.kind === 'line') { const l = schGetLine(sh, hit.id)!; beginGesture(); schDrag = { mode: 'move-line', id: hit.id, grab: paper, pts0: l.points.map((p) => ({ ...p })) }; }
  else if (hit.kind === 'dim') { beginSchMultiMove(sh, paper); }
  renderProperties(); invalidate();
  return true;
}

/** Every selected sheet object as it stands, so a multi-selection move applies
 *  one delta to all of them (a connection's bends move, its ends follow its boxes). */
function schMoveSnapshot(sh: Sheet): SheetSnapshot[] {
  return snapshotSheetObjects(sh, SHEET_OBJECTS, schemSelSet);
}
/** A press that moves every selected sheet object together, from `paper`: one
 *  gesture, from where each one was. */
export function beginSchMultiMove(sh: Sheet, paper: Vec2): void {
  beginGesture();
  schDrag = { mode: 'move-multi', grab: paper, items: schMoveSnapshot(sh) };
}

/** Continue a schematic gesture. Returns true when a draft / drag is active. */
export function schematicMouseMove(e: MouseEvent, sh: Sheet, pv: PaperView): boolean {
  const paper = screenToPaper(pv, screenPos(e));
  // A create tool hovers its SNAPPED point, so the preview, the readout chip and
  // the point a click places can never disagree — same as the model tools.
  schHover = schHoverPoint(paper, e.altKey);
  if (schTool === 'line' && !schDrag) updateDistEntry();
  if ((schTool === 'text' || schTool === 'rect' || schTool === 'ellipse' || schTool === 'leader' || schTool === 'viewport' || schTool === 'dim') && !schDrag) { invalidate(); return true; }
  if (sheetSchematic.schConnDraft) { sheetSchematic.schConnDraft.to = paper; invalidate(); return true; }
  if ((schTool === 'line' || schTool === 'text') && !schDrag) { invalidate(); return true; }
  const d = schDrag;
  if (!d) return false;
  if (d.mode === 'move-rect') {
    const rc = d.sel.kind === 'box' ? schGetBox(sh, d.sel.id) : schGetImage(sh, d.sel.id);
    if (rc) {
      let nx = d.x0 + (paper.x - d.grab.x), ny = d.y0 + (paper.y - d.grab.y);
      // Align a box's edges to other boxes' projections (Alt suspends).
      if (d.sel.kind === 'box' && !e.altKey) {
        const { dx, dy } = alignMoveSnap({ x0: nx, x1: nx + rc.w, y0: ny, y1: ny + rc.h }, otherSchBoxRects(sh, d.sel.id), ALIGN_TOL_PX / paperView().scale);
        nx += dx; ny += dy;
      } else state.alignGuides = [];
      rc.x = nx; rc.y = ny;
    }
  }
  else if (d.mode === 'move-line') { const l = schGetLine(sh, d.id); if (l) l.points = d.pts0.map((p) => ({ x: p.x + (paper.x - d.grab.x), y: p.y + (paper.y - d.grab.y) })); }
  else if (d.mode === 'move-multi') {
    moveFromSnapshot(sh, SHEET_OBJECTS, d.items, paper.x - d.grab.x, paper.y - d.grab.y);
  }
  else if (d.mode === 'resize') { schApplyResize(sh, d, paper, e.altKey); }
  else if (d.mode === 'vertex') {
    if (d.kind === 'line') { const l = schGetLine(sh, d.id); if (l) l.points[d.index] = schLineVertexSnap(paper, l, d.index, e.altKey); }
    else {
      const c = schContent(sh).conns.find((x) => x.id === d.id);
      if (c?.waypoints) {
        // Snap so the vertex's adjoining segments run H/V (Alt suspends). Route
        // is [from-anchor, ...waypoints, to-anchor]; neighbours of waypoint
        // d.index are route[d.index] and route[d.index+2].
        let p = paper;
        const route = e.altKey ? null : schConnRoute(sh, c);
        if (route) p = squareToNeighbours(paper, paper, route[d.index], route[d.index + 2], 8 / paperView().scale);
        c.waypoints[d.index] = p;
      }
    }
  }
  else if (d.mode === 'badge') {
    const c = schContent(sh).conns.find((x) => x.id === d.id);
    const route = c ? schConnRoute(sh, c) : null;
    // Store the badge's position as a fraction of the route length (kept just off
    // the ends so it never tucks under a box anchor).
    if (c && route) c.badgeT = Math.max(0.02, Math.min(0.98, nearestFractionOnPath(route, paper)));
  }
  invalidate();
  return true;
}

function schApplyResize(sh: Sheet, d: { sel: SchemSel; handle: string; orig: { x: number; y: number; w: number; h: number } }, paper: Vec2, noAlign = false): void {
  let { x, y, w, h } = resizeRect(d.orig, d.handle as HandleId, paper, 4);
  // Align the grabbed edges to other boxes' projections (paper Y-down; Shift suspends).
  if (d.sel.kind === 'box' && !noAlign) {
    const snapped = alignResizeSnap(
      { x0: x, x1: x + w, y0: y, y1: y + h }, otherSchBoxRects(sh, d.sel.id),
      { x0: d.handle.includes('w'), x1: d.handle.includes('e'), y0: d.handle.includes('n'), y1: d.handle.includes('s') },
      ALIGN_TOL_PX / paperView().scale);
    x = snapped.x0; y = snapped.y0; w = snapped.x1 - snapped.x0; h = snapped.y1 - snapped.y0;
  } else state.alignGuides = [];
  const rc = d.sel.kind === 'box' ? schGetBox(sh, d.sel.id) : schGetImage(sh, d.sel.id);
  if (rc) { rc.x = x; rc.y = y; rc.w = w; rc.h = h; }
}

/** Finish a schematic gesture. Returns true when one was in progress. */
export function schematicMouseUp(sh: Sheet, pv: PaperView): boolean {
  if (sheetSchematic.schConnDraft) {
    const cd = sheetSchematic.schConnDraft; sheetSchematic.schConnDraft = null;
    const hit = schHitObject(sh, cd.to, 3 / pv.scale);
    if (hit?.kind === 'box' && hit.id !== cd.fromId) {
      pushHistory();
      // Born with the last-edited connection's style (endpoints/label excluded).
      const conn: SchematicConn = {
        id: schUid('conn'), from: cd.fromId, to: hit.id, label: '',
        lines: lastConnStyle?.lines ?? 1, type: lastConnStyle?.type ?? 'electrical',
        color: lastConnStyle?.color, thickness: lastConnStyle?.thickness, style: lastConnStyle?.style,
      };
      schContent(sh).conns.push(conn);
      setSchemSel({ kind: 'conn', id: conn.id });
      endPaperOneShot(); // make the new connection immediately editable
      renderProperties();
    }
    invalidate();
    return true;
  }
  if (schDrag) { commitGesture(); schDrag = null; state.alignGuides = []; renderProperties(); invalidate(); return true; }
  return false;
}

/** Live preview of the schematic text box being placed — the paper-space twin of
 *  `pendingTextBoxPreview`. `y` is the TOP edge, which on paper is the smaller Y. */
function schPendingTextBox(): { x: number; y: number; w: number; h: number; leader?: Vec2 } | null {
  const d = sheetSchematic.schBoxDraft;
  if ((schTool !== 'text' && schTool !== 'rect' && schTool !== 'ellipse' && schTool !== 'leader' && schTool !== 'viewport') || !d?.a) return null; // a rectangle / viewport / circle previews as its box
  const b = d.b ?? schHover;
  if (!b) return null;
  if (schTool === 'ellipse') { const r = Math.hypot(b.x - d.a.x, b.y - d.a.y); return { x: d.a.x - r, y: d.a.y - r, w: 2 * r, h: 2 * r }; }
  if (schTool === 'leader') return { x: Math.min(d.a.x, b.x), y: Math.min(d.a.y, b.y), w: Math.abs(d.a.x - b.x), h: Math.abs(d.a.y - b.y), leader: d.leader };
  return {
    x: Math.min(d.a.x, b.x),
    y: Math.min(d.a.y, b.y),
    w: Math.abs(d.a.x - b.x),
    h: Math.abs(d.a.y - b.y),
    leader: d.b ? d.leader ?? schHover ?? undefined : undefined,
  };
}

/** Commit the in-progress schematic text box (corners + optional leader) and
 *  start typing in it, the same way `finishTextComment` does in the model. */
export function schFinishTextBox(sh: Sheet): void {
  const d = sheetSchematic.schBoxDraft;
  sheetSchematic.schBoxDraft = null;
  if (!d?.a || !d.b) { invalidate(); return; }
  pushHistory();
  const minW = 90 / paperView().scale, minH = 36 / paperView().scale; // usable at this zoom
  const box: SchematicBox = {
    id: schUid('box'),
    x: Math.min(d.a.x, d.b.x),
    y: Math.min(d.a.y, d.b.y),
    w: Math.max(minW, Math.abs(d.a.x - d.b.x)),
    h: Math.max(minH, Math.abs(d.a.y - d.b.y)),
    text: '',
    leader: d.leader,
  };
  schContent(sh).boxes.push(box);
  setSchemSel({ kind: 'box', id: box.id });
  endPaperOneShot(); // so its resize handles work the moment typing ends
  renderProperties();
  invalidate();
  schOpenBoxEditor(box, true); // creation already pushed history
}

/** Place a paper dimension from its two points and the click that sets its line. */
function schFinishDim(sh: Sheet, a: Vec2, b: Vec2, at: Vec2): void {
  sheetSchematic.schDimDraft = null;
  if (Math.hypot(b.x - a.x, b.y - a.y) < 0.5) { invalidate(); return; }
  const kind = inferDimKind(a, b, at);
  pushHistory();
  const dm: SchematicDim = { id: schUid('dim'), a: { ...a }, b: { ...b }, offset: paperDimOffset(a, b, at, kind) };
  if (kind !== 'aligned') dm.kind = kind;
  (schContent(sh).dims ??= []).push(dm);
  setSchemSel({ kind: 'dim', id: dm.id });
  endPaperOneShot();
  renderProperties();
  invalidate();
}

/** Model vertices as they land on this sheet, through each viewport (paper mm) —
 *  what a paper dimension snaps to, AutoCAD's trans-spatial snap. Cached by the
 *  drawing revision and the sheet, since `snapCandidates` walks the whole model. */
let schModelSnapCache: { key: string; pts: Vec2[] } | null = null;
function schModelVertsOnPaper(sh: Sheet): Vec2[] {
  const key = `${drawingRev()}|${sh.id}|${JSON.stringify(sh.viewports)}|${(sh.hiddenLayers ?? []).join(',')}|${sh.showCeilings === true}`;
  if (schModelSnapCache?.key === key) return schModelSnapCache.pts;
  // Only what the sheet's viewports draw: its own hidden layers are left out too.
  const world = snapCandidates(null, new Set(sh.hiddenLayers ?? []), sh).map((c) => c.p);
  const pts: Vec2[] = [];
  for (const v of sh.viewports) {
    for (const w of world) {
      const q = viewportFrame(v).toPaper(w);
      if (q.x >= v.x && q.x <= v.x + v.w && q.y >= v.y && q.y <= v.y + v.h) pts.push(q);
    }
  }
  schModelSnapCache = { key, pts };
  return pts;
}

/** A paper dimension's point snap: sheet objects, and the model through viewports. */
function schDimSnap(paper: Vec2, shift: boolean): Vec2 {
  const sh = activeSheet();
  if (shift || !sh) return paper;
  return nearestVertexSnap(paper, [...schSnapVerts(sh), ...schModelVertsOnPaper(sh)], 8 / paperView().scale);
}

/** The paper Circle: a centre and a rim point make an elliptical box (a circle;
 *  its handles then stretch it into an ellipse), with no text. */
function schFinishCircle(sh: Sheet): void {
  const d = sheetSchematic.schBoxDraft;
  sheetSchematic.schBoxDraft = null;
  if (!d?.a || !d.b) { invalidate(); return; }
  const r = Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y);
  if (r < 0.5) { invalidate(); return; }
  pushHistory();
  const box: SchematicBox = { id: schUid('box'), x: d.a.x - r, y: d.a.y - r, w: 2 * r, h: 2 * r, text: '', shape: 'ellipse' };
  schContent(sh).boxes.push(box);
  setSchemSel({ kind: 'box', id: box.id });
  endPaperOneShot();
  renderProperties();
  invalidate();
}

/** The paper Rectangle: a box with no text, placed by two corners (the model
 *  Rectangle's gesture); it is selected and nothing opens for typing. */
function schFinishRect(sh: Sheet): void {
  const d = sheetSchematic.schBoxDraft;
  sheetSchematic.schBoxDraft = null;
  if (!d?.a || !d.b) { invalidate(); return; }
  const w = Math.abs(d.a.x - d.b.x), h = Math.abs(d.a.y - d.b.y);
  if (w < 0.5 || h < 0.5) { invalidate(); return; }
  pushHistory();
  const box: SchematicBox = { id: schUid('box'), x: Math.min(d.a.x, d.b.x), y: Math.min(d.a.y, d.b.y), w, h, text: '' };
  schContent(sh).boxes.push(box);
  setSchemSel({ kind: 'box', id: box.id });
  endPaperOneShot();
  renderProperties();
  invalidate();
}

/** The Viewport tool: the rectangle drawn on the page becomes a viewport onto the
 *  whole model, unlocked (so it can be entered and panned straight away). */
function schFinishViewport(sh: Sheet): void {
  const d = sheetSchematic.schBoxDraft;
  sheetSchematic.schBoxDraft = null;
  if (!d?.a || !d.b) { invalidate(); return; }
  const w = Math.abs(d.a.x - d.b.x), h = Math.abs(d.a.y - d.b.y);
  if (w < 10 || h < 10) { flashToolStatus(tr('Draw the viewport at least 10 mm each way.')); invalidate(); return; }
  pushHistory();
  const v = makeViewport(sh, null);
  v.x = Math.min(d.a.x, d.b.x); v.y = Math.min(d.a.y, d.b.y); v.w = w; v.h = h;
  const b = modelBoundsOrNull();
  if (b) fitViewportToBounds(v, b);
  clampViewportToContent(v, sh);
  sh.viewports.push(v);
  endPaperOneShot();
  setSchemSel({ kind: 'viewport', id: v.id });
  renderProperties();
  invalidate();
}

/** Finish the in-progress schematic line (needs >= 2 vertices). */
export function schFinishLine(sh: Sheet): void {
  if (sheetSchematic.schLineDraft.length >= 2) {
    pushHistory();
    const l: SchematicLine = { id: schUid('line'), points: sheetSchematic.schLineDraft.map((p) => ({ ...p })), color: '#1b1d22', thickness: 1, style: 'solid' };
    if (schLineClosed && l.points.length >= 3) l.closed = true; // the paper Polygon
    schContent(sh).lines.push(l);
    setSchemSel({ kind: 'line', id: l.id });
    endPaperOneShot(); // make the new line immediately editable
    renderProperties();
  }
  sheetSchematic.schLineDraft = [];
  state.distEntry = null; updateDistEntry();
  invalidate();
}

/** Where a sheet box's inline editor sits and how it looks: over the box, its
 *  caret sized to the box's text. The box's text and caret are drawn live on the
 *  canvas at their true size (drawBox), so the editor's own glyphs and caret are
 *  hidden: it only takes the keystrokes and holds the selection. */
function schBoxLook(box: SchematicBox, pv: PaperView): EditorLook {
  const tl = paperToScreen(pv, box.x, box.y);
  return {
    left: `${tl.x}px`, top: `${tl.y}px`,
    width: `${Math.max(24, box.w * pv.scale)}px`, height: `${Math.max(20, box.h * pv.scale)}px`,
    fontSize: `${Math.max(1, boxFontMm(box) * pv.scale)}px`, lineHeight: '1.2', padding: '2px',
    border: 'none', background: 'transparent', color: 'transparent', caretColor: 'transparent', textAlign: 'center',
  };
}
/** Edit a sheet box's text inline (the text boxes' editor). `isNew`: a box just
 *  made — its text rides its creation's undo step. Sheet boxes are multi-line:
 *  Enter is a new line, Ctrl/Cmd+Enter, Escape or a click away commits. */
export function schOpenBoxEditor(box: SchematicBox, isNew = false): void {
  textEditorUi.close(true);
  const id = box.id;
  const now = (): SchematicBox | undefined => { const sh = activeSheet(); return sh ? schGetBox(sh, id) : undefined; };
  let look: EditorLook = schBoxLook(box, paperView());
  schEditBoxId = id;
  textEditorUi.open({
    text: box.text,
    look: () => { const b = now(); if (b) look = schBoxLook(b, paperView()); return look; },
    keys: { enter: 'ctrl-commits', esc: 'commits' },
    gesture: !isNew,
    caret: 'end',
    input: (t) => { const b = now(); if (b) { b.text = t; invalidate(); } },
    caretMoved: () => { schCaretOn = true; invalidate(); },
    commit: (t) => { const b = now(); if (b) b.text = t; },
    closed: () => { schEditBoxId = null; stopSchCaretBlink(); renderProperties(); invalidate(); },
  });
  // Blink the canvas caret (re-render ~2×/s; the render reads selectionEnd).
  stopSchCaretBlink();
  schCaretTimer = window.setInterval(() => { schCaretOn = !schCaretOn; invalidate(); }, 530);
}
export function schCloseBoxEditor(commit = true): void {
  if (schEditBoxId != null) textEditorUi.close(commit);
}

// Style of the last connection the user edited, so a NEW connection is born with
// it instead of the bare electrical default. Only the style axes carry over —
// never the endpoints, label or waypoints.
let lastConnStyle: Pick<SchematicConn, 'type' | 'color' | 'lines' | 'thickness' | 'style'> | null = null;
export function rememberConnStyle(cn: SchematicConn): void {
  lastConnStyle = { type: cn.type, color: cn.color, lines: cn.lines, thickness: cn.thickness, style: cn.style };
}

/** Delete every selected schematic object (a box also drops its connections);
 *  `pick` false (Cut) takes a view whole, whatever is picked inside it. */
export function deleteSchematicSelected(sh: Sheet, pick = true): void {
  if (!schemSelSet.length) return;
  if (pick && elecDeletePick(sh)) return; // what is picked inside a view goes on its own
  if (!deletableSheetObjects(sh, SHEET_OBJECTS, schemSelSet).length) return; // a locked viewport stays
  pushHistory();
  elecViewsGo(schemSelSet.flatMap((s) => (s.kind === 'elec' ? (elecViewById(sh, s.id) ?? []) : [])));
  // What stood on a deleted box (its connections) goes with it.
  deleteSheetObjects(sh, SHEET_OBJECTS, schemSelSet);
  setSchemSel(null);
  renderProperties(); invalidate();
}

const SCH_OBJ_PASTE_OFFSET = 8; // paper mm shift per successive paste

/** Copy every selected schematic object to the in-app schematic clipboard. */
export function copySchematicSelection(): void {
  const sh = activeSheet(); if (!sh || titleEditor.titleEdit || !schemSelSet.length) return;
  const items = copySheetObjects(sh, SHEET_OBJECTS, schemSelSet);
  if (!items.length) return;
  takeClipboard('sheet');
  clipboardUi.schObjClipboard = items;
  clipboardUi.schObjPasteCount = 0;
}

/** Paste the sheet clipboard into the active sheet, offset and selected — on any
 *  sheet. A copied connection rebinds to the copies of its boxes; one to a box
 *  not copied pastes only if that box is on the target sheet. */
export function pasteSchematicClipboard(at?: Vec2): void {
  const sh = activeSheet(); if (!sh || titleEditor.titleEdit) return;
  if (!clipboardUi.schObjClipboard?.length) { flashClipboardElsewhere('sheet'); return; }
  // Centred on `at` (paper mm) when given — Ctrl+V with the pointer on the sheet —
  // else stepped away from the originals.
  const b = at ? sheetClipBounds(SHEET_OBJECTS, clipboardUi.schObjClipboard) : null;
  const step = SCH_OBJ_PASTE_OFFSET * (clipboardUi.schObjPasteCount + 1);
  const d = at && b ? { x: at.x - (b.min.x + b.max.x) / 2, y: at.y - (b.min.y + b.max.y) / 2 } : { x: step, y: step };
  const from = undoSteps.mark(); // a step only when something lands
  const added = pasteSheetObjects(sh, SHEET_OBJECTS, clipboardUi.schObjClipboard, d.x, d.y, schUid);
  if (!added.length) return;
  undoSteps.push(from);
  if (!at) clipboardUi.schObjPasteCount++;
  setSchemSelSet(added);
  renderProperties(); invalidate();
}

/** Scale a schematic box's text size (and the box around its centre) — the
 *  right-click Enlarge/Reduce action, mirroring the drawing's text boxes. */
function scaleSchBox(b: SchematicBox, factor: number): void {
  const cur = boxFontPt(b);
  const next = Math.max(2, Math.min(200, cur * factor));
  const f = next / cur;
  if (f === 1) return;
  pushHistory();
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2; // paper space is Y-down
  b.fontPt = next; b.fontScale = undefined;
  b.w *= f; b.h *= f;
  b.x = cx - b.w / 2; b.y = cy - b.h / 2;
  renderProperties(); invalidate();
}

/** Right-click on a sheet: the object under the cursor (or the selection), the
 *  core, then a text box's own Enlarge / Reduce. */
export function showSheetObjectMenu(e: MouseEvent, sh: Sheet): void {
  const paper = screenToPaper(paperView(), screenPos(e));
  const hit = sheetTableAt(sh, paperView(), screenPos(e)) ? null : schHitObject(sh, paper, 3 / paperView().scale); // a table is over them
  if (hit && !schemSelHas(hit)) { setSchemSel(hit); renderProperties(); invalidate(); }
  const ev = hit ? null : elecViewAt(sh, paper, paperView());
  if (ev) {
    if (!(schemSelSet.length > 1 && schemSelHas({ kind: 'elec', id: ev.view.id }))) { setSchemSel({ kind: 'elec', id: ev.view.id }); elecSheet.elecSub = elecPickOf(ev.hit); }
    renderProperties(); invalidate();
  }
  const own: CtxItem[] = ev ? elecMenuItems() : [];
  const box = schemSelSet.length === 1 && schemSel?.kind === 'box' ? schGetBox(sh, schemSel.id) : undefined;
  if (box) {
    own.push({ label: 'Enlarge text (×2)', run: () => scaleSchBox(box, 2) });
    own.push({ label: 'Reduce text (÷2)', run: () => scaleSchBox(box, 0.5) });
  }
  openContextMenu(e, [...coreMenuItems('sheet', paper), own]);
}

// ---- Copy / paste style between schematic objects ----
interface SchStyle { color?: string; fillColor?: string; textColor?: string; textOpacity?: number; fillOpacity?: number; borderOpacity?: number; fontPt?: number; cornerRadius?: number; thickness?: number; style?: SchematicLine['style']; lines?: number; type?: SchematicConn['type']; }
let schStyleClipboard: SchStyle | null = null;

/** Capture the selected object's colours / style / text size into the clipboard. */
function copySchStyle(sh: Sheet, sel: SchemSel): void {
  const c = schContent(sh);
  if (sel.kind === 'box') { const b = c.boxes.find((x) => x.id === sel.id); if (b) schStyleClipboard = { color: b.borderColor, fillColor: b.fillColor, textColor: b.textColor, textOpacity: b.textOpacity, fillOpacity: b.fillOpacity, borderOpacity: b.borderOpacity, fontPt: boxFontPt(b), cornerRadius: b.cornerRadius }; }
  else if (sel.kind === 'line') { const l = c.lines.find((x) => x.id === sel.id); if (l) schStyleClipboard = { color: l.color, thickness: l.thickness, style: l.style }; }
  else if (sel.kind === 'conn') { const cn = c.conns.find((x) => x.id === sel.id); if (cn) schStyleClipboard = { color: cn.color, type: cn.type, lines: cn.lines, thickness: cn.thickness, style: cn.style }; }
}

/** Apply the clipboard style to the selected object (fields relevant to its kind). */
function pasteSchStyle(sh: Sheet, sel: SchemSel): void {
  const s = schStyleClipboard; if (!s) return;
  const c = schContent(sh);
  if (sel.kind === 'box') {
    const b = c.boxes.find((x) => x.id === sel.id); if (!b) return;
    pushHistory();
    if ('color' in s && s.color) b.borderColor = s.color;
    if ('fillColor' in s) b.fillColor = s.fillColor;
    if ('textColor' in s) b.textColor = s.textColor;
    if ('textOpacity' in s) b.textOpacity = s.textOpacity;
    if ('fillOpacity' in s) b.fillOpacity = s.fillOpacity;
    if ('borderOpacity' in s) b.borderOpacity = s.borderOpacity;
    if ('fontPt' in s) { b.fontPt = s.fontPt; b.fontScale = undefined; }
    if ('cornerRadius' in s) b.cornerRadius = s.cornerRadius;
  } else if (sel.kind === 'line') {
    const l = c.lines.find((x) => x.id === sel.id); if (!l) return;
    pushHistory();
    if ('color' in s && s.color) l.color = s.color;
    if ('thickness' in s && s.thickness != null) l.thickness = s.thickness;
    if ('style' in s && s.style) l.style = s.style;
  } else if (sel.kind === 'conn') {
    const cn = c.conns.find((x) => x.id === sel.id); if (!cn) return;
    pushHistory();
    if ('color' in s) cn.color = s.color;
    if ('type' in s && s.type) cn.type = s.type;
    if ('lines' in s && s.lines != null) cn.lines = s.lines;
    if ('thickness' in s && s.thickness != null) cn.thickness = s.thickness;
    if ('style' in s && s.style) cn.style = s.style;
  }
  renderProperties(); invalidate();
}

/** The four dash styles of a sheet's lines, connections and schematic wires. */
export const LINE_STYLES = [['solid', 'Solid'], ['dashed', 'Dashed'], ['dotted', 'Dotted'], ['dash-dot', 'Dash-dot']] as const;
/** The dash-style dropdown, shared by the schematic line, connection and wire
 *  panels so all offer the same four styles. */
export function lineStyleRow(current: SchematicLineStyle, onPick: (v: SchematicLineStyle) => void, label = 'Style'): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr(label);
  const sel = document.createElement('select');
  for (const [v, t] of LINE_STYLES) {
    const o = document.createElement('option');
    o.value = v; o.textContent = tr(t);
    if (current === v) o.selected = true;
    sel.appendChild(o);
  }
  sel.addEventListener('change', () => onPick(sel.value as SchematicLineStyle));
  row.append(lab, sel);
  return row;
}

/** Copy/Paste-style button pair, shown in every schematic object's properties. */
export function appendSchStyleButtons(body: HTMLElement, sh: Sheet, sel: SchemSel): void {
  const row = document.createElement('div');
  row.style.display = 'flex'; row.style.gap = '6px'; row.style.margin = '6px 0';
  const mk = (label: string, fn: () => void, enabled: boolean): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.textContent = label; btn.style.flex = '1'; btn.disabled = !enabled;
    btn.addEventListener('click', fn);
    return btn;
  };
  row.appendChild(mk(tr('Copy style'), () => { copySchStyle(sh, sel); renderProperties(); }, true));
  row.appendChild(mk(tr('Paste style'), () => pasteSchStyle(sh, sel), !!schStyleClipboard));
  body.appendChild(row);
}

/** Draw in-progress schematic drafts (box rectangle, line/hover, connection). */
export function drawSchematicDrafts(pv: PaperView): void {
  ctx.save();
  ctx.setLineDash([]);
  // The SAME preview the model text tool draws: dashed box plus, once both
  // corners are down, the leader arrow tracking the cursor.
  const pendingBox = schPendingTextBox();
  if (pendingBox) drawPendingTextBox(ctx, (p) => paperToScreen(pv, p.x, p.y), pv.scale, pendingBox);
  // A paper dimension being placed: the run so far, then the dimension itself
  // following the cursor — the same drawing as the placed one.
  const shd = activeSheet();
  if (schTool === 'dim' && sheetSchematic.schDimDraft?.a && schHover && shd) {
    const { a } = sheetSchematic.schDimDraft;
    if (!sheetSchematic.schDimDraft.b) drawPendingLine(ctx, (p) => paperToScreen(pv, p.x, p.y), [a], schHover);
    else {
      const b = sheetSchematic.schDimDraft.b, kind = inferDimKind(a, b, schHover);
      drawPaperDim(ctx, pv, { id: 'draft', a, b, offset: paperDimOffset(a, b, schHover, kind), kind }, shd.viewports, '#4aa3ff');
    }
  }
  // The SAME preview the model line tool draws — dashed run, vertex dots and the
  // cursor readout — projected into paper space instead of world space.
  if (schTool === 'line' && (sheetSchematic.schLineDraft.length || schHover)) {
    drawPendingLine(ctx, (p) => paperToScreen(pv, p.x, p.y), sheetSchematic.schLineDraft, distEntryPoint() ?? schHover);
  }
  const proj = (p: Vec2): Vec2 => paperToScreen(pv, p.x, p.y);
  if (sheetHover) drawHoverOutline(ctx, proj, sheetHover.ring, sheetHover.closed);
  if (sheetSchematic.sheetMarquee) drawMarquee(ctx, proj(sheetSchematic.sheetMarquee.a), proj(sheetSchematic.sheetMarquee.b), sheetSchematic.sheetMarquee.a.x <= sheetSchematic.sheetMarquee.b.x);
  if (sheetSchematic.schConnDraft) {
    const from = schGetBox(activeSheet()!, sheetSchematic.schConnDraft.fromId);
    if (from) { const a = paperToScreen(pv, schBoxCenter(from).x, schBoxCenter(from).y), b = paperToScreen(pv, sheetSchematic.schConnDraft.to.x, sheetSchematic.schConnDraft.to.y); ctx.strokeStyle = '#4aa3ff'; ctx.setLineDash([6, 4]); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
  }
  ctx.restore();
}
